import {EditState} from '../models/edit-types';
import {AppState, Board, Column, createIdentity, EntityID, Task} from '../models/types';
import {TaskEditController} from './TaskEditController';
import {ResponsiveState, VIEW_CONFIGS, ViewMode, ViewModeEnum} from '../models/view-types';
import BoardStorage from '../storage/BoardStorage';
import SyncService from '../storage/SyncService';
import TaskStorage from '../storage/TaskStorage';

import {OrderManager} from '../utils/OrderManager';
import {ColumnOrderManager} from '../utils/ColumnOrderManager';
import {AppSignals} from './AppSignals';
import {runAsyncTask} from "./runAsyncTask";
import Database from "../storage/database";
import {ExchangeContainer} from "./DataExchange";
import database from "../storage/database";
import {cast} from "../utils/cast";
import {TaskOrderProcessor} from '../utils/TaskOrderProcessor';
import {defaultTask} from "../storage/createDefaultBoard";

export type FilterByMode = 'all' | 'unsynced' | 'recent';

export interface ITaskEditController {
	// Состояние
	// Фильтры для режима списка
	filters: {
		column: string | null;
		status: FilterByMode;
	};
}

export interface TaskUi
{
	isHighlighted: boolean
}
export class App {
	private static instance: App;
	private state: AppState;
	private boardStorage: BoardStorage;
	private taskStorage: TaskStorage;
	private syncService: SyncService;
	private currentBoardSnapshot: Board | null = null; // Для отката оптимистичных обновлений
	private taskEditController: TaskEditController;

	// Фильтры для режима списка
	private listFilters: ITaskEditController['filters'] = {
		column: null,
		status: 'all'
	};

	private taskOrderProcessor = TaskOrderProcessor.getInstance();
	private taskUi = new Map<EntityID, TaskUi>();

	private constructor() {
		this.boardStorage = new BoardStorage();
		this.taskStorage = new TaskStorage();
		this.syncService = new SyncService();

		// Состояние сначала пустое
		this.state = {
			currentBoards: [{
				...createIdentity(''),
				title: "",
				tasks: [],
				columns: [],
			}],
			activeBoardIndex: 0,
			viewMode: "columns",
			lastSyncTime: null,
			isLoading: true,
			showFilters: false
		} as AppState;

		// Инициализация контроллера редактирования задач
		this.taskEditController = new TaskEditController(
			(id) => this.getTaskById(id),
			(id, updates) => this.taskUpdateOptimistic(id, updates),
			(id) => this.taskDeletePermanently(id)
		);

		// Инициализация асинхронная
		this.init();
	}

	//<editor-fold desc="Вспомогательные методы для оптимистичных обновлений">
	private async withOptimisticUpdate<T>(
		operation: () => Promise<T>,
		optimisticUpdate: () => void,
		onSuccess?: (result: T) => void,
		onError?: (error: Error) => void
	): Promise<T> {
		// Сохраняем снимок для отката
		const board = this.getCurrentBoard();
		this.currentBoardSnapshot = JSON.parse(JSON.stringify(board));

		// Выполняем оптимистичное обновление
		optimisticUpdate();

		// Уведомляем об изменении
		AppSignals.boardUpdated.emit(board);

		try {
			// Выполняем асинхронную операцию
			const result = await operation();

			// Успешно - сбрасываем снимок
			this.currentBoardSnapshot = null;

			// Вызываем коллбэк успеха
			if (onSuccess) {
				onSuccess(result);
			}

			return result;
		} catch (error) {
			console.error('Error in optimistic update:', error);

			// Откатываем изменения
			if (this.currentBoardSnapshot) {
				board.columns = this.currentBoardSnapshot.columns;
				board.tasks = this.currentBoardSnapshot.tasks;
				this.currentBoardSnapshot = null;

				AppSignals.boardUpdated.emit(board);
			}

			// Вызываем коллбэк ошибки
			if (onError) {
				onError(error as Error);
			}

			throw error;
		}
	}

	private emitBoardUpdate(): void {
		runAsyncTask(AppSignals.boardUpdated.name, () => {
			AppSignals.boardUpdated.emit(App.getInstance().getCurrentBoard());
		});
	}

	private saveBoardAfterOperation(): void {
		const board :Board = {
			...this.getCurrentBoard(),
			isEditing: undefined
		};
		this.boardStorage.saveBoard(board).catch(error => {
			console.error('Error saving board after operation:', error);
		});
	}
	//</editor-fold>

	private async init(): Promise<void> {
		// Загружаем состояние из IndexedDB
		this.state = await this.loadInitialState();

		// Инициализация адаптивности
		this.responsiveInit();

		// Слушаем сеть
		this.setupNetworkListeners();

		// Запускаем фоновую синхронизацию
		this.syncService.startBackgroundSync();

		// Уведомляем, что доска загружена
		AppSignals.appInvalidated.emit();
		AppSignals.responsiveStateChanged.emit(this.getResponsiveState());
	}


	static getInstance(): App {
		if (!App.instance) {
			App.instance = new App();
			(window as any).app = App.instance;
		}
		return App.instance;
	}

	private async initDatabase(): Promise<void> {
		try {
			// Инициализация происходит автоматически при первом обращении
			// Просто проверяем доступность
			await this.boardStorage.getBoard('board_1');
			console.log('Database initialized successfully');
		} catch (error) {
			console.error('Failed to initialize database:', error);
			// Можно показать уведомление пользователю
		}
	}

	private setupNetworkListeners(): void {
		window.addEventListener('online', () => {
			console.log('App is online, starting sync...');
			this.syncService.forceSync().catch(console.error);
			AppSignals.networkStatus.emit(true);
		});

		window.addEventListener('offline', () => {
			console.log('App is offline');
			AppSignals.networkStatus.emit(false);
		});
	}

	//<editor-fold desc="Методы для работы с фильтрами">
	getFilters(): ITaskEditController['filters'] {
		return {...this.listFilters};
	}

	setFilterColumn(columnId: string | null): void {
		this.listFilters.column = columnId;
		AppSignals.appInvalidated.emit();
	}

	setFilterStatus(status: 'all' | 'unsynced' | 'recent'): void {
		this.listFilters.status = status;
		AppSignals.appInvalidated.emit();
	}

	clearFilters(): void {
		this.listFilters = {column: null, status: 'all'};
		AppSignals.appInvalidated.emit();
	}
	//</editor-fold>

	//<editor-fold desc="Публичные методы для работы с задачами ">
	async getTaskById(taskId: string): Promise<Task | null> {
		const board = this.getCurrentBoard();
		return board.tasks.find(t => t?.id === taskId) || null;
	}

	// noinspection JSMethodCanBeStatic
	private taskDeletePermanently(id: string):Promise<void> {
		(new TaskStorage()).deleteTask(id);
		return Promise.resolve();
	}

	async taskUpdateOptimistic(taskId: string, updates: Partial<Task>): Promise<Task> {
		const board = this.getCurrentBoard();
		const taskIndex = board.tasks.findIndex(t => t?.id === taskId);

		if (taskIndex === -1) {
			throw new Error(`Task ${taskId} not found`);
		}
		const original = board.tasks[taskIndex];
		const updatedTask: Task = {
			...original,
			...updates,
			boardId: board.id,
			updatedAt: new Date(),
			isSynced: false
		};
		const targetColumnId = updates.columnId || updatedTask.columnId;
		const oldColumnId = original.columnId;
		const newIndex = updates.order || updatedTask.order;
		const isNewColumn = updates.columnId !== original.columnId;


		return this.withOptimisticUpdate<Task>(
			// Асинхронная операция
			async () => {
				const savedTask = await this.taskStorage.updateTaskOptimistic(taskId, updates);
				if (!savedTask || !savedTask.task) {
					// noinspection ExceptionCaughtLocallyJS
					throw new Error("Error updating task");
				}
			//	console.log("saved", savedTask.task);
				if (!savedTask.task?.deletedAt) {
					//updated
					board.tasks[taskIndex] = {...savedTask.task, boardId: board.id} as Task;
				}
				return savedTask.task;
			},
			// Оптимистичное обновление
			() => {
				//console.log("save", original.columnId, updatedTask.columnId, updates);

				board.tasks[taskIndex] = updatedTask;
				if (updatedTask.deletedAt) {
					//task deleted
					delete board.tasks[taskIndex];
				} else {
					board.tasks[taskIndex] = updatedTask;
				}
				//console.log("board", board.tasks.map(t => t.title));
			},
			// Успешное завершение
			(result) => {
				this.currentBoardSnapshot = null;

				if (!(updates.columnId !== undefined ||
					updates.order !== undefined)) {
					this.emitBoardUpdate();
					return;
				}

				//need to move task.
				this.taskOrderProcessor.updateTaskPosition(
					taskId,
					targetColumnId,
					newIndex,
					board.tasks,
					this.taskStorage
				).then(() => {
					// Обновление оперативных кешей
					if (isNewColumn) {
						// Если колонка меняется, обновляем кеши обеих колонок
						this.taskOrderProcessor.invalidateColumns([oldColumnId, targetColumnId]);
					} else {
						// Если колонка не меняется, обновляем только один кеш
						this.taskOrderProcessor.invalidateColumn(targetColumnId);
					}
					this.emitBoardUpdate()
					this.setTaskHighlight(updatedTask, true);
				});


			}
		);
	}

	/** Task action can be only on the current board.
	 *
	 * @param columnId
	 * @param title
	 */
	async taskCreate(columnId: string, title: string): Promise<Task> {
		const board = this.getCurrentBoard();

		// Регистрация (новый ID, метки времени и тд)
		const columnTasks = board.tasks.filter(t => t.columnId === columnId);
		const order = OrderManager.getOrderForNewTask(columnTasks);

		let localId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		const tempTask: Task = {
			...defaultTask,
			id: localId,
			localId: localId,


			title,
			description: '',

			columnId,
			boardId: board.id,
			order,
		};

		// Обновление оперативных данных
		board.tasks.push(tempTask);

		AppSignals.boardUpdated.emit(board);

		// Обновление БД (и отправка на синхронизацию)
		try {
			const savedTask = await this.taskStorage.createTask(tempTask);

			//
			// Обновить ID задачи
			const taskIndex = board.tasks.findIndex(t => t && t.id === tempTask.id);
			if (taskIndex !== -1) {
				board.tasks[taskIndex] = savedTask;
				let taskId = savedTask.id;
				this.setTaskHighlight(savedTask, true);
			}
			// Отправить сигнал для подсветки и скролла к созданной задаче
			setTimeout(() => {
				AppSignals.highlightAndScrollToTask.emit({
					taskId: savedTask.id,
					columnId: savedTask.columnId,
					tempId: tempTask.id
				});
			}, 0);

			// Обновление оперативных кешей
			this.taskOrderProcessor.invalidateColumn(columnId);
			AppSignals.boardUpdated.emit(board);

			return savedTask;
		} catch (error) {
			// Откат при ошибке
			board.tasks = board.tasks.filter(t => t.id !== tempTask.id);

			AppSignals.boardUpdated.emit(board);
			throw error;
		}
	}

	public setTaskHighlight(task: Task, value: boolean) {
		this.taskUi.set(task.id, {
			isHighlighted: value
		});
		if (!value){
			this.taskUi.delete(task.id);
		}
		AppSignals.taskUpdated.emit(task);
	}
	public getTaskHighlight(task:Task):boolean{
		return this.taskUi.get(task.id)?.isHighlighted || false;
	}


// Алиас для совместимости с TaskAdd компонентом
	async taskAdd(columnId: string, title: string): Promise<Task> {
		return this.taskCreate(columnId, title);
	}

	async taskMove(taskId: string, targetColumnId: string, newIndex: number): Promise<Task> {
		const board = this.getCurrentBoard();
		const task = board.tasks.find(t => t.id === taskId);

		if (!task) {
			return Promise.resolve(cast(task));
		}

		const oldColumnId = task.columnId;
		return this.taskUpdateOptimistic(taskId, {
			columnId: targetColumnId,
			order: task.order,
			updatedAt: new Date(),
			isSynced: false,
		})
	}

	async taskReorder(taskId: string, newIndex: number): Promise<Task> {
		return this.taskUpdateOptimistic(taskId, {
			order: newIndex
		});
		//
		// const board = this.getCurrentBoard();
		// const task = board.tasks.find(t => t.id === taskId);
		//
		// if (!task) {
		// 	return;
		// }
		//
		// const columnId = task.columnId;
		//
		// return this.withOptimisticUpdate<void>(
		// 	// Асинхронная операция - обновление БД
		// 	async () => {
		// 		await this.taskStorage.updateTaskOptimistic(taskId, {
		// 			order: task.order,
		// 			updatedAt: new Date(),
		// 			isSynced: false,
		// 		});
		// 	},
		// 	// Оптимистичное обновление - обновление оперативных данных
		// 	async () => {
		// 		// Обновить позицию задачи
		// 		await this.taskOrderProcessor.updateTaskPosition(
		// 			taskId,
		// 			columnId,
		// 			newIndex,
		// 			board.tasks,
		// 			this.taskStorage
		// 		);
		//
		// 		// Обновление оперативных кешей
		// 		this.taskOrderProcessor.invalidateColumn(columnId);
		// 	}
		// );
	}
	//</editor-fold>

	//<editor-fold desc="Публичные методы для работы с колонками">
	async columnAdd(title: string): Promise<Column> {
		const board = this.getCurrentBoard();

		return this.withOptimisticUpdate<Column>(
			// Асинхронная операция
			async () => {
				const savedColumn = await this.boardStorage.saveColumn({
					...createIdentity(`col_temp_${Date.now()}`),
					title: title || 'Новая колонка',
					order: 0, // Будет обновлено в процессе
					boardId: board.id,
				});

				// Обновляем ID колонки в локальном состоянии
				const columnIndex = board.columns.findIndex(c => c.id === savedColumn.id);
				if (columnIndex !== -1) {
					board.columns[columnIndex] = savedColumn;
				}

				return savedColumn;
			},
			// Оптимистичное обновление
			() => {
				// Вычисляем order для новой колонки
				const order = ColumnOrderManager.getOrderForNewColumn(board.columns);

				// Проверяем, не нужна ли перевыдача
				const columnsWithNewOne = [
					...board.columns,
					{...board.columns[0], order} as Column // Временный объект для проверки
				];

				const needsReorder = ColumnOrderManager.needsReorder(columnsWithNewOne);
				const finalOrder = needsReorder
					? board.columns.length * 10  // Если нужна перевыдача, просто вычисляем по позиции
					: order;

				// Оптимистичное обновление
				const newColumn: Column = {
					...createIdentity( `col_temp_${Date.now()}`),
					title: title || 'Новая колонка',
					order: finalOrder,
					boardId: board.id,
				};

				board.columns.push(newColumn);

				// Если нужна была перевыдача, делаем её для всех колонок
				if (needsReorder) {
					const redistributed = ColumnOrderManager.redistributeOrders(board.columns);
					board.columns = redistributed;
				}

				this.editState.hasChanges = true;
			},
			// Успешное завершение
			(result) => {
				this.emitBoardUpdate();
			}
		);
	}

	async columnDelete(columnId: string): Promise<void> {
		const board = this.getCurrentBoard();

		// Нельзя удалить последнюю колонку
		if (board.columns.length <= 1) {
			throw new Error('Cannot delete the last column');
		}

		return this.withOptimisticUpdate<void>(
			// Асинхронная операция
			async () => {
				// Удаляем колонку из базы
				await this.boardStorage.deleteColumn(columnId);

				// Сохраняем обновленные задачи
				for (const task of board.tasks.filter(t => t.columnId === columnId)) {
					const targetColumn = board.columns.find(c => c.id === task.columnId);
					if (targetColumn) {
						await this.taskStorage.updateTaskOptimistic(task.id, {
							boardId: board.id,
							columnId: targetColumn.id,
							updatedAt: new Date(),
							isSynced: false,
						});
					}
				}

				// Сохраняем обновленные колонки
				for (const col of board.columns) {
					await this.boardStorage.updateColumn(col.id, {
						order: col.order
					});
				}
			},
			// Оптимистичное обновление
			() => {
				const columnIndex = board.columns.findIndex(c => c.id === columnId);
				if (columnIndex === -1) {
					return;
				}

				const column = board.columns[columnIndex];

				// Оптимистичное обновление: перемещаем задачи в предыдущую колонку
				const columnTasks = board.tasks.filter(t => t.columnId === column.id);
				if (columnTasks.length > 0) {
					const targetColumnIndex = Math.max(0, columnIndex - 1);
					const targetColumn = board.columns[targetColumnIndex];

					// Обновляем задачи
					columnTasks.forEach(task => {
						task.columnId = targetColumn.id;
						task.isSynced = false;
					});
				}

				// Удаляем колонку
				board.columns.splice(columnIndex, 1);

				// Перевыдаем порядок колонок с правильным order
				const redistributed = ColumnOrderManager.redistributeOrders(board.columns);
				board.columns = redistributed;

				this.editState.hasChanges = true;
			}
		);
	}
	//</editor-fold>

	//<editor-fold desc="Методы для синхронизации">
	async getSyncStatus() {
		return this.syncService.getSyncStatus();
	}

	async forceSync(): Promise<void> {
		return this.syncService.forceSync();
	}
	//</editor-fold>

	//<editor-fold desc="Геттеры">
	getCurrentBoard(): Board {
		return this.state.currentBoards[this.state.activeBoardIndex];
	}

	getBoardById(boardId: EntityID) {
		return this.state.currentBoards.find(board => board.id === boardId);
	}

	getBoardStorage(): BoardStorage {
		return this.boardStorage;
	}

	getTaskStorage(): TaskStorage {
		return this.taskStorage;
	}

	getSyncService(): SyncService {
		return this.syncService;
	}
	getState():Readonly<AppState>
	{
		return this.state;
	}
	getViewMode(): ViewMode {
		return this.state.viewMode;
	}

	//</editor-fold>

	//<editor-fold desc="Начальное состояние">
	private async loadInitialState(): Promise<AppState> {
		const defaultState = {
			viewMode: this.getPreferredViewMode() || ViewModeEnum.List,
			activeBoardIndex: 0,
			isLoading: false,
			lastSyncTime: null,
			showFilters: false
		};

		try {
			// Попробуем загрузить реальную доску
			const boards = await this.boardStorage.getAllBoards();
			if ( boards.length > 0) {
				const board = await this.boardStorage.getBoard(boards[0].id);
				if (board) {
					//console.log('Loaded board from IndexedDB:', board);
					return {
						...defaultState,
						currentBoards: [board],
					};
				}
			}
		} catch (error) {
			console.warn('No board found in IndexedDB, creating default board', error);
		}

		// Если нет данных — создаем мок
		const createdBoard = await this.boardStorage.createBoard(this.createMockBoard());
		const starterAppState:AppState = {
			...defaultState,
			currentBoards: [createdBoard],
		};


		return starterAppState;
	}


	// Создание тестовой доски
	private createMockBoard(): Board {
		let boardId = 'board1';
		const column1: Column = {
			...createIdentity( 'col1'),
			title: 'To Do',
			order: 0,
			boardId: boardId,
		};

		const column2: Column = {
			...createIdentity( 'col2'),
			title: 'In Progress',
			order: 1,
			boardId: boardId,
		};

		const tasks: Task[] = [
			{
				...defaultTask,
				...createIdentity( ''),
				id: 'task1',
				localId: 'local_1',
				serverId: null,
				title: 'Создать базовый каркас',
				description: 'Реализовать модели данных и App синглтон',
				columnId: 'col1',

			},
			{
				...defaultTask,
				...createIdentity( ''),
				id: 'task2',
				localId: 'local_2',
				title: 'Настроить проект',
				description: 'Установить ParcelJS и Tailwind',
				columnId: 'col1',
			},
			{
				...defaultTask,
				...createIdentity( ''),
				id: 'task3',
				localId: 'local_3',
				title: 'Создать компоненты',
				description: 'Board, Column, TaskCard',
				columnId: 'col2',
			},
		].map( task=>{
			return {...task, boardId:boardId}
		});

		return {
			...createIdentity(boardId),
			title: 'Проект: Доска задач',
			columns: [column1, column2],
			tasks: tasks,
			isEditing: false,
			updatedAt: new Date(),
			createdAt: new Date(),
		};
	}
	//</editor-fold>

	// Публичные сеттеры
	boardSetTitle(title: string): void {
		const board = this.getCurrentBoard();
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			throw new Error('Board title cannot be empty');
		}

		if (board.title === trimmedTitle) {
			return;
		}

		// Оптимистичное обновление
		board.title = trimmedTitle;

		// Отмечаем изменения в режиме редактирования
		if (board.isEditing && this.editState.originalBoard) {
			this.editState.hasChanges = true;
		}

		// Эмитируем сигнал обновления доски
		this.emitBoardUpdate();

		// Сохраняем в IndexedDB
		this.saveBoardAfterOperation();
	}

	//<editor-fold desc="Edit mode">
	toggleEditMode(): void {
		const board = this.getCurrentBoard();
		board.isEditing = !board.isEditing;
		// TODO: Добавить систему сигналов на этапах 2-3
	}

	private editState: EditState = {
		originalBoard: null,
		hasChanges: false,
		isSaving: false,
		lastSaved: null,
	};
	// Проверка наличия изменений
	hasUnsavedChanges(): boolean {
		return this.editState.hasChanges;
	}

	// Получение состояния редактирования
	getEditState(): EditState {
		return {...this.editState};
	}


	// Вход/выход из режима редактирования
	editModeStart(): void {
		const board = this.getCurrentBoard();
		//		console.log('Starting edit mode, current board:', board);

		// Сохраняем глубокую копию оригинальной доски
		this.editState.originalBoard = JSON.parse(JSON.stringify(board));
		board.isEditing = true;
		this.editState.hasChanges = false;
		this.editState.isSaving = false;

		//		console.log('Edit state after start:', this.editState);

		AppSignals.boardUpdated.emit(board);
	}

	editModeCancel(): void {
		if (this.editState.isSaving) {
			return;
		}

		const board = this.getCurrentBoard();

		// Восстанавливаем оригинальное состояние
		if (this.editState.originalBoard) {
			const original = this.editState.originalBoard;
			board.columns = original.columns;
			board.tasks = original.tasks;
			board.title = original.title;
		}

		board.isEditing = false;
		this.editState.originalBoard = null;
		this.editState.hasChanges = false;

		AppSignals.boardUpdated.emit(board);
	}

	editModeSave(): Promise<void> {
		return new Promise((resolve, reject) => {
			if (this.editState.isSaving) {
				reject(new Error('Already saving'));
				return;
			}

			const board = this.getCurrentBoard();
			this.editState.isSaving = true;
			this.boardStorage.saveBoard(board);

			// Симуляция асинхронного сохранения
			setTimeout(() => {
				board.isEditing = false;
				this.editState.originalBoard = null;
				this.editState.hasChanges = false;
				this.editState.isSaving = false;
				this.editState.lastSaved = new Date();

				AppSignals.boardUpdated.emit(board);
				resolve();
			}, 500); // Имитация задержки сети
		});
	}
	//</editor-fold>

	//<editor-fold desc="Columns actions">
	async columnMove(fromIndex: number, toIndex: number): Promise<void> {
		const board = this.getCurrentBoard();

		if (fromIndex < 0 || fromIndex >= board.columns.length ||
			toIndex < 0 || toIndex >= board.columns.length ||
			fromIndex === toIndex) {
			return;
		}

		return this.withOptimisticUpdate<void>(
			// Асинхронная операция
			async () => {
				// Сохраняем все колонки
				for (const column of board.columns) {
					await this.boardStorage.updateColumn(column.id, {
						order: column.order,
						boardId: board.id
					});
				}
			},
			// Оптимистичное обновление
			() => {
				// Получаем колонки для вычисления order (без перемещаемой колонки)
				const columnsWithoutMoved = board.columns.filter((_, index) => index !== fromIndex);

				// Получаем соседние колонки для целевой позиции
				const adjacent = ColumnOrderManager.getAdjacentColumns(columnsWithoutMoved, toIndex);
				let newOrder = ColumnOrderManager.getOrderBetweenColumns(adjacent.prevColumn, adjacent.nextColumn);

				// Если нужна перевыдача order
				if (newOrder === -1) {
					// Добавляем колонку в правильную позицию для перевыдачи
					const movedColumn = board.columns[fromIndex];
					const columnsForRedistribution = [...columnsWithoutMoved];
					columnsForRedistribution.splice(toIndex, 0, movedColumn);
					const redistributed = ColumnOrderManager.redistributeOrders(columnsForRedistribution);
					newOrder = redistributed.find(c => c.id === movedColumn.id)?.order || 0;

					// Обновляем order для всех колонок
					board.columns = redistributed;
				} else {
					// Просто перемещаем колонку и обновляем order
					const [movedColumn] = board.columns.splice(fromIndex, 1);
					movedColumn.order = newOrder;
					board.columns.splice(toIndex, 0, movedColumn);
				}

				this.editState.hasChanges = true;
			}
		);
	}
	/** Метод для переименования колонки
	 * @param columnId
	 * @param newTitle
	 */
	async columnRename(columnId: string, newTitle: string): Promise<Column> {
		const board = this.getCurrentBoard();
		const columnIndex = board.columns.findIndex(col => col.id === columnId);

		if (columnIndex === -1) {
			throw new Error(`Column ${columnId} not found`);
		}

		const trimmedTitle = newTitle.trim();
		if (!trimmedTitle) {
			throw new Error('Column title cannot be empty');
		}

		return this.withOptimisticUpdate<Column>(
			// Асинхронная операция
			async () => {
				const result = await this.boardStorage.updateColumn(columnId, { title: trimmedTitle });
				if (!result) {
					throw new Error('Error updating column');
				}
				return result;
			},
			// Оптимистичное обновление
			() => {
				// Оптимистичное обновление
				const originalColumn = board.columns[columnIndex];
				const updatedColumn: Column = {
					...originalColumn,
					title: trimmedTitle,
				};

				board.columns[columnIndex] = updatedColumn;
			},
			// Успешное завершение
			(result) => {
				this.emitBoardUpdate();
			}
		);
	}


	//</editor-fold>

	//<editor-fold desc="Responsive mode and view mode">
	private responsiveState: ResponsiveState = {
		currentViewMode: ViewModeEnum.Columns,
		availableModes: [ViewModeEnum.Columns, ViewModeEnum.List],
		screenWidth: window.innerWidth,
		screenHeight: window.innerHeight,
		isPortrait: window.innerHeight > window.innerWidth,
		touchSupported: 'ontouchstart' in window,
		gesturesEnabled: true,
	};

	private responsiveInit(): void {
		// Определяем начальный режим
		this.determineViewMode();

		// Слушаем изменения размера окна
		window.addEventListener('resize', this.handleResize.bind(this));

		// Слушаем изменения ориентации
		window.addEventListener('orientationchange', this.handleOrientationChange.bind(this));

		// Предотвращаем масштабирование на мобильных при двойном тапе
		if (this.responsiveState.touchSupported) {
			document.addEventListener('touchmove', this.preventZoomOnDrag.bind(this), {passive: false});
		}
	}

	private determineViewMode(): void {
		const width = window.innerWidth;

		// Автоматическое определение режима по ширине экрана
		let newMode: ViewMode = ViewModeEnum.List;

		if (width < 768) {
			newMode = ViewModeEnum.List; // По умолчанию список на очень узких экранах
		} else {
			newMode = ViewModeEnum.Columns;
		}

		this.setViewMode(newMode, 'auto');
	}

	private handleResize(): void {
		const newWidth = window.innerWidth;
		const newHeight = window.innerHeight;

		this.responsiveState.screenWidth = newWidth;
		this.responsiveState.screenHeight = newHeight;
		this.responsiveState.isPortrait = newHeight > newWidth;

		// Автоматически переключаем режим при изменении размера (только если в режиме auto)
		if (this.responsiveState.currentViewMode === ViewModeEnum.Columns && newWidth < 1024) {
			this.setViewMode(ViewModeEnum.List, 'auto');
		} else if (this.responsiveState.currentViewMode === ViewModeEnum.List && newWidth >= 1024) {
			this.setViewMode(ViewModeEnum.Columns, 'auto');
		}

		AppSignals.responsiveStateChanged.emit(this.getResponsiveState());
	}

	private handleOrientationChange(): void {
		setTimeout(() => {
			this.handleResize();
		}, 100);
	}
	private preventZoomOnDrag(event: TouchEvent): void {
		if (event.touches.length > 1) {
			event.preventDefault();
		}
	}

	// Публичные методы для управления режимами
	setViewMode(mode: ViewMode, source: 'auto' | 'manual' = 'manual'): void {
		if (!this.responsiveState.availableModes.includes(mode)) {
			console.warn(`View mode ${mode} is not available`);
			return;
		}

		this.responsiveState.currentViewMode = mode;
		this.state.viewMode = mode;

		// Сохраняем выбор пользователя в localStorage
		if (source === 'manual') {
			localStorage.setItem('preferredViewMode', mode);
		}

		// Обновляем CSS классы на body для глобальных стилей
		this.updateBodyClasses();

		AppSignals.viewModeChanged.emit(mode);
		AppSignals.responsiveStateChanged.emit(this.getResponsiveState());
	}

	toggleViewMode(): void {
		const currentIndex = this.responsiveState.availableModes.indexOf(
			this.responsiveState.currentViewMode
		);
		const nextIndex = (currentIndex + 1) % this.responsiveState.availableModes.length;
		const nextMode = this.responsiveState.availableModes[nextIndex];

		this.setViewMode(nextMode, 'manual');
	}

	private updateBodyClasses(): void {
		const {currentViewMode, touchSupported} = this.responsiveState;
		const body = document.body;

		// Удаляем предыдущие классы режимов
		body.classList.remove('view-columns', 'view-list');

		// Добавляем текущий класс режима
		body.classList.add(`view-${currentViewMode}`);

		// Добавляем класс для тач-устройств
		if (touchSupported) {
			body.classList.add('touch-device');
		} else {
			body.classList.remove('touch-device');
		}

		// Добавляем класс для ориентации
		if (this.responsiveState.isPortrait) {
			body.classList.add('portrait');
			body.classList.remove('landscape');
		} else {
			body.classList.add('landscape');
			body.classList.remove('portrait');
		}
	}

	getCurrentViewMode(): ViewMode {
		return this.responsiveState.currentViewMode;
	}

	getViewConfig() {
		return VIEW_CONFIGS[this.responsiveState.currentViewMode];
	}

	getResponsiveState(): ResponsiveState {
		return {...this.responsiveState};
	}

	isTouchDevice(): boolean {
		return this.responsiveState.touchSupported;
	}

	// Метод для получения предпочтительного режима из localStorage
	getPreferredViewMode(): ViewMode | null {
		const saved = localStorage.getItem('preferredViewMode');
		return saved && this.responsiveState.availableModes.includes(saved as ViewMode)
			? (saved as ViewMode)
			: null;
	}
	//</editor-fold>

	//<editor-fold desc=" Контроллер редактирования задач">
	getTaskEditController(): TaskEditController {
		return this.taskEditController;
	}
	//</editor-fold>

	//<editor-fold desc="Методы для работы с мобильным списком">

	getFilteredTasks(columnId?: string): Task[] {
		const board = this.getCurrentBoard();
		const { column, status } = this.listFilters;

		let tasks = board.tasks;

		// Фильтр по колонке
		if (column) {
			tasks = tasks.filter(task => task.columnId === column);
		} else if (columnId) {
			tasks = tasks.filter(task => task.columnId === columnId);
		}

		// Фильтр по статусу
		if (status === 'unsynced') {
			tasks = tasks.filter(task => !task.isSynced);
		} else if (status === 'recent') {
			const yesterday = new Date();
			yesterday.setDate(yesterday.getDate() - 1);
			tasks = tasks.filter(task => new Date(task.updatedAt) > yesterday);
		}

		// Сортировка по order
		return tasks.sort((a, b) => a.order - b.order);
	}

	getListStats(): { total: number; unsynced: number; recent: number; columns: number } {
		const board = this.getCurrentBoard();
		const tasks = board.tasks;

		const total = tasks.length;
		const unsynced = tasks.filter(t => !t.isSynced).length;

		const yesterday = new Date();
		yesterday.setDate(yesterday.getDate() - 1);
		const recent = tasks.filter(t => new Date(t.updatedAt) > yesterday).length;

		return {
			total,
			unsynced,
			recent,
			columns: board.columns.length
		};
	}

	setShowFilters(showFilter: boolean) {
		this.state.showFilters = showFilter;
		AppSignals.filtersToggled.emit(showFilter);
	}
	//</editor-fold>

	//<editor-fold desc="Методы для экспорта/импорта/очистки данных">

	/**
	 * Экспорт всех данных приложения
	 */
	async exportData(): Promise<ExchangeContainer> {
		const boards :Board[] = await this.boardStorage.getAllBoards();

		const fullBoards: Board[] = [];
		for (const board of boards){
			console.log(`Exporting board ${board.id} `)
			fullBoards.push(cast(await this.boardStorage.getBoard(board.id)));
		}
		const container: ExchangeContainer = {
			version: '1.0',
			timestamp: new Date().toISOString(),
			boards: fullBoards
		}
		return container;
	}


	/**
	 * Импорт данных в приложение
	 * @param importData - данные для импорта
	 */
	async importData(importData: ExchangeContainer): Promise<void> {
		if (!importData || !importData.version) {
			throw new Error('Неверный формат данных для импорта');
		}

		// Проверяем версию данных
		if (importData.version !== '1.0') {
			throw new Error(`Неподдерживаемая версия данных: ${importData.version}. Поддерживаемая версия: 1.0`);
		}

		try {
			Database.getInstance().deleteDatabase();
			Database.getInstance().createDatabase();

			// Сохраняем доски в хранилище
			for (const board of importData.boards) {
				await this.boardStorage.saveBoard(board);
			}

			window.location.reload();

			// Уведомляем об обновлении
			AppSignals.appInvalidated.emit();
			AppSignals.boardUpdated.emit(App.getInstance().getCurrentBoard());
		} catch (error) {
			console.error('Ошибка при импорте данных:', error);
			throw error;
		}
	}

	/**
	 * Очистка всех данных приложения
	 */
	async eraseAllData(): Promise<void> {
		try {
			Database.getInstance().deleteDatabase();
			window.location.reload();
		} catch (error) {
			console.error('Ошибка при очистке данных:', error);
			throw error;
		}
	}
	//</editor-fold>
}
